import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { getCustomers, getConnections } from '../../api'
import { customerListFrom, listFrom, loadErrorMessage } from '../../utils/loadState'
import { connectionHealth, formatShortDateTime } from '../../utils/connectionHealth'
import './AdminConnectors.css'

export default function AdminConnectors() {
  const [customers, setCustomers] = useState([])
  const [allConnections, setAllConnections] = useState([])
  const [loading, setLoading] = useState(true)
  // A failed load is an error with a Retry. It used to fall back to a table of made-up
  // sample connections -- a failure dressed up as data (item 201).
  const [loadError, setLoadError] = useState(null)
  // Customers whose connection list could not be read: named, never counted as "none".
  const [failedCustomers, setFailedCustomers] = useState([])

  async function load() {
    setLoading(true)
    setLoadError(null)
    setFailedCustomers([])
    try {
      const list = customerListFrom(await getCustomers())
      setCustomers(list)

      const failed = []
      const connPromises = list.map(c => {
        const name = c.company_name || c.name || c.customer_id
        return getConnections(c.customer_id || c.id)
          .then(r => listFrom(r, 'connections').map(conn => ({
            ...conn,
            customer_name: name,
            customer_id: c.customer_id || c.id,
          })))
          .catch(() => { failed.push(name); return [] })
      })
      const results = await Promise.all(connPromises)
      setAllConnections(results.flat())
      setFailedCustomers(failed)
    } catch (err) {
      console.error('Failed to load:', err)
      setCustomers([])
      setAllConnections([])
      setLoadError(loadErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  // Health is the connection's newest sync run (`last_run` on GET
  // /api/connector/connections/{id}, utils/connectionHealth). `test_status` is written when
  // the credentials are saved and never by a sync, so it says nothing about health.
  const displayConnections = allConnections.map(c => ({ ...c, health: connectionHealth(c) }))

  const healthyCt = displayConnections.filter(c => c.health.tone === 'healthy').length
  const partialCt = displayConnections.filter(c => c.health.tone === 'partial').length
  const errorCt = displayConnections.filter(c => c.health.tone === 'error').length

  const supportedVendors = [
    { name: 'UKG Pro', active: true },
    { name: 'UKG WFM', active: true },
    { name: 'ADP Workforce Now', active: true },
    { name: 'Workday HCM', active: true },
    { name: 'SAP SuccessFactors', active: true },
    { name: 'Oracle HCM', active: false },
    { name: 'Ceridian Dayforce', active: false },
    { name: 'Paychex Flex', active: false },
    { name: 'Paylocity', active: false },
    { name: 'BambooHR', active: false },
  ]

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Connectors Overview' },
  ]
  const navActions = (
    <>
      <Link to="/admin" className="nav-btn">← Dashboard</Link>
      <button className="nav-btn primary">+ New Connection</button>
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="ac-loading"><div className="ac-spinner" /><p>Loading connectors…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="ac-page">
        <div className="ac-header">
          <div>
            <h1 className="ac-title">Connectors Overview</h1>
            <p className="ac-subtitle">All system connections across all customers</p>
          </div>
        </div>

        {loadError && <LoadError what="connectors" error={loadError} onRetry={load} />}
        {failedCustomers.length > 0 && (
          <LoadError
            what={`connections for ${failedCustomers.length} customer${failedCustomers.length !== 1 ? 's' : ''}`}
            error={failedCustomers.join(', ')}
            onRetry={load}
          />
        )}

        {/* Health Summary */}
        <div className="ac-health-grid">
          <div className="ac-health-card">
            <div className="ac-health-value green">{healthyCt}</div>
            <div className="ac-health-label">Healthy</div>
          </div>
          <div className="ac-health-card">
            <div className="ac-health-value amber">{partialCt}</div>
            <div className="ac-health-label">Last sync had errors</div>
          </div>
          <div className="ac-health-card">
            <div className="ac-health-value red">{errorCt}</div>
            <div className="ac-health-label">Last sync failed</div>
          </div>
          <div className="ac-health-card">
            <div className="ac-health-value">{loadError ? '—' : displayConnections.length}</div>
            <div className="ac-health-label">Total</div>
          </div>
        </div>

        {/* Connections Table */}
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Vendor</th>
                <th>Product</th>
                <th>Status</th>
                <th>Last Sync</th>
                <th>Next Run</th>
                <th>Tables (last sync)</th>
                <th>Rows (last sync)</th>
              </tr>
            </thead>
            <tbody>
              {!loadError && displayConnections.length === 0 && (
                <tr><td colSpan={8} className="ac-empty">No connections{failedCustomers.length ? ' could be read' : ' yet'}.</td></tr>
              )}
              {displayConnections.map((c, i) => (
                <tr key={i}>
                  <td className="ac-customer-name">{c.customer_name}</td>
                  <td>{c.vendor || c.vendor_name || c.vendor_id || '—'}</td>
                  <td>{c.product || c.product_name || c.product_id || '—'}</td>
                  <td>
                    <span className={`ac-status-badge ${c.health.tone}`} title={c.health.readError || c.health.error || c.health.line}>
                      <span className={`ac-status-dot ${c.health.tone}`}></span>
                      {c.health.statusWord}
                    </span>
                  </td>
                  <td className="ac-mono">{c.health.lastSyncAt ? `${formatShortDateTime(c.health.lastSyncAt)} (${c.last_run?.trigger_kind || 'manual'})` : '—'}</td>
                  <td className="ac-mono">{c.schedule ? (c.schedule.enabled ? formatShortDateTime(c.schedule.next_run_at) : 'schedule off') : (c.schedule_error ? 'unreadable' : 'not scheduled')}</td>
                  <td>{c.last_run?.tables_processed != null ? `${c.last_run.tables_processed}/${c.last_run.total_tables ?? '?'}` : '—'}</td>
                  <td className="ac-mono">{c.last_run?.total_rows != null ? c.last_run.total_rows.toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Supported Vendors */}
        <div className="ac-vendors-section">
          <div className="ac-subsection-title">Supported Vendors</div>
          <div className="ac-vendor-pills">
            {supportedVendors.map(v => (
              <span key={v.name} className={`ac-vendor-pill ${v.active ? 'active' : ''}`}>{v.name}</span>
            ))}
          </div>
          <div className="ac-vendor-hint">Green = in use · Gray = available</div>
        </div>
      </div>
    </>
  )
}
